import type {
  WPTableColumn,
  WPTableColumnHeading,
  WPTableRow,
} from '@ria-client/supergraph/structuredContent/hydration/WPTable/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ria-client/tw-shadcn/components/table';
import { AccessibleHiddenText } from '@ria-client/tw-shared-ui';
import { ScrollableRegion } from '@ria-client/tw-shared-ui/layout/scrollable-region';
import { StructuredContentRenderer } from '../renderer';
import type { NodeProps } from '../types';

export type WPTableProps = NodeProps<{
  headers?: WPTableColumnHeading[];
  rows?: WPTableRow[];
  tableBolding?: '' | 'NONE' | 'FIRST_ROW' | 'FIRST_COLUMN' | 'FIRST_ROW_COLUMN';
  isStriped?: boolean;
  rowHover?: boolean;
}>;

export function isBold(tableBolding: string | undefined, rowIndex: number, columnIndex: number) {
  return (
    (tableBolding === 'FIRST_ROW' && rowIndex === 0) ||
    (tableBolding === 'FIRST_COLUMN' && columnIndex === 0) ||
    (tableBolding === 'FIRST_ROW_COLUMN' && (rowIndex === 0 || columnIndex === 0))
  );
}

export function WPTable({ props }: WPTableProps) {
  return (
    <ScrollableRegion
      aria-label="Scrollable table"
      className="border border-border border-t-2 border-t-primary"
    >
      <Table
        data-striped={props.isStriped ? '' : undefined}
        data-row-hover={props.rowHover ? '' : undefined}
        className="w-full caption-bottom text-sm [&_tbody_tr]:bg-background [&[data-striped]_tbody_tr:nth-of-type(even)]:bg-[#f7f7f7] [&[data-row-hover]_tbody_tr:hover_td]:bg-secondary"
      >
        {props.headers && (
          <TableHeader>
            <TableRow>
              {props.headers.map((header, index) => (
                <TableHead key={`sc-table-header-${index}`} colSpan={header.colspan}>
                  {header.content ? (
                    <StructuredContentRenderer doc={header.content} />
                  ) : (
                    <AccessibleHiddenText>Empty Table Header</AccessibleHiddenText>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {props.rows?.map((row: WPTableRow, rowIndex) => (
            <TableRow key={`sc-table-row-${rowIndex}`}>
              {row.columns.map((column: WPTableColumn, columnIndex) => (
                <TableCell
                  key={`sc-table-col-${column.id}`}
                  colSpan={column.colspan}
                  className={
                    isBold(props.tableBolding, rowIndex, columnIndex)
                      ? 'font-bold [&_*]:font-bold'
                      : undefined
                  }
                >
                  {column.content && <StructuredContentRenderer doc={column.content} />}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ScrollableRegion>
  );
}
